import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Copy, Check, Mail, Info } from "lucide-react";
import { toast } from "sonner";

interface LeadEmailAddressProps {
  inboundEmailId: string | null;
  inboundDomain?: string;
  className?: string;
}

// Inbound email domain for receiving lead-specific emails
const DEFAULT_INBOUND_DOMAIN = "leads.creativeinvestments.africa";

export const LeadEmailAddress = ({ 
  inboundEmailId, 
  inboundDomain = DEFAULT_INBOUND_DOMAIN,
  className = ""
}: LeadEmailAddressProps) => {
  const [copied, setCopied] = useState(false);

  if (!inboundEmailId) {
    return null;
  }

  const emailAddress = `lead-${inboundEmailId}@${inboundDomain}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(emailAddress);
      setCopied(true);
      toast.success("Email address copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy email address");
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <Badge 
                variant="outline" 
                className="font-mono text-xs cursor-pointer hover:bg-muted transition-colors"
                onClick={handleCopy}
              >
                {emailAddress}
              </Badge>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs">
            <p className="font-medium">Lead Email Address</p>
            <p className="text-xs text-muted-foreground mt-1">
              CC this address on all client emails to keep correspondence in one place.
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6"
        onClick={handleCopy}
      >
        {copied ? (
          <Check className="h-3 w-3 text-green-500" />
        ) : (
          <Copy className="h-3 w-3" />
        )}
      </Button>

      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className="h-4 w-4 text-muted-foreground cursor-help" />
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs">
            <p className="text-xs">
              All emails sent to this address will automatically appear in the Emails tab.
              Use it in CC when emailing clients to keep all communication centralized.
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};
